import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useNewConversationalCRMFlowEnabled} from 'src/hooks/useCrm';

import css from './tasks.css';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {
  CollapsibleCard,
  CollapsibleCardContent,
} from '@spaced-out/ui-design-system/lib/components/CollapsibleCard';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
import {useSelector} from 'react-redux';
import {selectUserInfo} from 'src/selectors/crm';
import {
  getDashboardTasks,
  INITIAL_APPLICANT_LIST_FILTER_DATA,
  OFFER_STAGE_TASKS,
} from 'src/components/crm/constant';
import {Separator} from '@spaced-out/ui-design-system/lib/components/Separator';
import {
  SubTitleSmall,
  BodyMedium,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {SemanticIcon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {Badge} from '@spaced-out/ui-design-system/lib/components/Badge';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {sizeFluid} from '@spaced-out/ui-design-system/lib/styles/variables/_size';
import {borderWidthPrimary} from '@spaced-out/ui-design-system/lib/styles/variables/_border';
import {checkIfIntegrationTypeSF} from 'src/utils/crm-new/common';

import {useHistory} from 'src/rerouter/index';


type TaskConfig = {
  iconName?: string;
  label?: string;
  sortOrder?: number;
};

type TaskData = {
  stageActionStatus: string;
  milestoneId: string;
  count: number;
  jobId: number;
  jobTitle: string;
};

type TasksProps = {
  tasksData: Array<TaskData> | null;
  isLoading: boolean;
};

type AggregatedTask = TaskData & {
  milestoneId: string;
};

const getJobsForTask = (
  tasksData: Array<TaskData>,
  stageActionStatus: string,
): Array<{jobId: number; jobTitle: string; count: number}> => {
  const jobMap = new Map<
    number,
    {jobId: number; jobTitle: string; count: number}
  >();
  tasksData.forEach((task) => {
    if (
      task.stageActionStatus !== stageActionStatus ||
      !task.jobId ||
      !task.jobTitle
    ) {
      return;
    }
    const existing = jobMap.get(task.jobId);
    if (existing) {
      existing.count += task.count;
    } else {
      jobMap.set(task.jobId, {
        jobId: task.jobId,
        jobTitle: task.jobTitle,
        count: task.count,
      });
    }
  });
  return Array.from(jobMap.values());
};

export const Tasks = ({tasksData = [], isLoading}: TasksProps) => {
  const {t} = useLabelTranslation();
  const isCrmVoiceFlowIntegrated = useNewConversationalCRMFlowEnabled();
  const history = useHistory();
  const userInfo = useSelector(selectUserInfo);
  const {organization: organizationInfo} = userInfo ?? {};
  const DASHBOARD_TASKS = getDashboardTasks(
    t,
    isCrmVoiceFlowIntegrated,
    userInfo,
  );

  const aggregatedTasks = (Array.isArray(tasksData) ? tasksData : [])
    .filter((task) => DASHBOARD_TASKS[task.stageActionStatus])
    .filter(
      (task) =>
        !checkIfIntegrationTypeSF(organizationInfo?.integrationType) ||
        task.stageActionStatus === 'OFFER_ACCEPTED_BY_CANDIDATE' ||
        // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type '"GENERATE_OFFER" | "OFFER_ACCEPTED_BY_CANDIDATE" | "OFFER_SENT_TO_CANDIDATE" | "REVIEW_OFFER" | "SEND_OFFER_TO_CANDIDATE" | "REQUEST_FOR_OFFER_APPROVAL" | "REQUESTED_FOR_APPROVAL"'.
        !OFFER_STAGE_TASKS.includes(task.stageActionStatus),
    )
    .reduce<Array<any>>((acc: Array<AggregatedTask>, task: TaskData) => {
      const existingTask = acc.find(
        (t) => t.stageActionStatus === task.stageActionStatus,
      );

      if (existingTask) {
        existingTask.count += task.count;
        existingTask.milestoneId = `${existingTask.milestoneId},${task.milestoneId}`;
      } else {
        acc.push({...task});
      }
      return acc;
    }, []);
  const sortedPendingTasks: Array<AggregatedTask> = aggregatedTasks.sort(
    (a: AggregatedTask, b: AggregatedTask) => {
      const taskA: TaskConfig = DASHBOARD_TASKS[a.stageActionStatus];
      const taskB: TaskConfig = DASHBOARD_TASKS[b.stageActionStatus];
      const orderA: number = taskA.sortOrder ?? Infinity;
      const orderB: number = taskB.sortOrder ?? Infinity;
      return orderA - orderB;
    },
  );

  const totalValidTasks = sortedPendingTasks.reduce(
    (sum, task) => sum + task.count,
    0,
  );

  const handleJobClick = (jobId: number, stageActionStatus: string) => {
    if (isCrmVoiceFlowIntegrated) {
      const routeParams = new URLSearchParams();
      routeParams.set('selectedStageActionStatus', stageActionStatus);
      history.push(`/crm/job/${jobId}/info/tasks?${routeParams.toString()}`);
    } else {
      const routeParams = new URLSearchParams();
      routeParams.set(
        'applicantFilters',
        JSON.stringify({
          ...INITIAL_APPLICANT_LIST_FILTER_DATA,
          filter: {
            ...INITIAL_APPLICANT_LIST_FILTER_DATA.filter,
            stepStatuses: [stageActionStatus],
          },
        }),
      );
      history.push(
        `/crm/job/${jobId}/info/applicants?${routeParams.toString()}`,
      );
    }
  };

  return (
    <>
      <Card classNames={{wrapper: css.card}}>
        <CardHeader>
          <CardTitle className={css.cardTitleContainer}>
            <SemanticIcon
              name="check"
              semantic="neutral"
              type="solid"
              size="small"
            />
            <SubTitleSmall>{t('All_Tasks', 'All Tasks')}</SubTitleSmall>
            {sortedPendingTasks.length > 0 && (
              <Badge text={String(totalValidTasks)} fill="blue" />
            )}
          </CardTitle>
        </CardHeader>
        {isLoading ? (
          <div className={css.loaderContainer}>
            <CircularLoader size="large" />
          </div>
        ) : (
          <CardContent className={css.innerCard}>
            {sortedPendingTasks.length > 0 ? (
              <div className={css.tasksWrapper}>
                {sortedPendingTasks.map((task: AggregatedTask, index) => {
                  const taskConfig: TaskConfig =
                    DASHBOARD_TASKS[task.stageActionStatus];
                  const jobs = getJobsForTask(
                    Array.isArray(tasksData) ? tasksData : [],
                    task.stageActionStatus,
                  );
                  return (
                    <>
                      <CollapsibleCard
                        title={
                          <>
                            <>{taskConfig.label || task.stageActionStatus}</>{' '}
                            <Badge text={String(task.count)} fill="blue" />
                          </>
                        }
                      >
                        <CollapsibleCardContent className={css.jobRowContainer}>
                          {jobs.map((job) => (
                            <div
                              key={job.jobId}
                              className={css.jobRow}
                              onClick={() =>
                                handleJobClick(
                                  job.jobId,
                                  task.stageActionStatus,
                                )
                              }
                            >
                              <BodyMedium>{job.jobTitle}</BodyMedium>
                              <Badge text={String(job.count)} fill="blue" />
                            </div>
                          ))}
                        </CollapsibleCardContent>
                      </CollapsibleCard>
                    </>
                  );
                })}
              </div>
            ) : (
              <EmptyState
                description={`${t(
                  'No_tasks_pending_Enjoy_the_break',
                  'No tasks pending. Enjoy the break',
                )}!`}
                imageVariant="data"
              />
            )}
          </CardContent>
        )}
      </Card>
    </>
  );
};
